<script lang="ts" setup>
  import type { ICloseArgs } from '#shared/types/modal.types';
  import type { FormError } from '@nuxt/ui';
  import type { ICurrencyData } from '~/store/cashier';
  import { LazyModalKYC, LazyModalRedemptionConfirmation, LazyModalRedemptionRequirements } from '#components';
  import { useAppToast } from '~/composables/shared/useAppToast';
  import { useCashierStore } from '~/store/cashier';
  import { preventNegative } from '~/utils/inputValidation';

  interface IFormData {
    currency?: ICurrencyData;
    address: string;
    amount?: number;
  }

  const emit = defineEmits<{
    close: [ICloseArgs];
  }>();

  const cashierStore = useCashierStore();
  const gtag = useGtag();

  const useKYCChecks = useRuntimeConfig().public.cashierKYCWithdrawalOnly;

  const { showErrorToast } = useAppToast();

  const state = ref<IFormData>({
    currency: undefined,
    address: '',
    amount: undefined,
  });
  const selectorOpen = ref<boolean>(false);
  const maxBalance = computed(() => stores.user.withdrawableBalance / 100);
  const minWithdrawAmount = computed(() => stores.firebase.affiliateGlobalConfig.minAmountWithdraw);

  const estimatedAmount = ref<number>(0);
  const estimatedFeeAmount = ref<number>(0);

  const estimatesLoading = ref<boolean>(false);
  const isWithdrawalRequestPending = ref<boolean>(false);

  const isFormValid = ref<boolean>(false);

  const amountPlaceholder = computed(() => {
    if (useKYCChecks && !stores.user.isKycApproved) {
      return '$ Enter Amount <span>(<span class="text-[#fa0215]">KYC required</span>)</span>';
    }
    return '$ Enter Amount';
  });

  const isWithdrawButtonEnabled = computed(() => {
    return isFormValid.value && !estimatesLoading.value && !isWithdrawalRequestPending.value;
  });

  onMounted(() => {
    stores.firebase.getGlobalConfig();
    stores.firebase.loadAffiliateGlobalConfig();
  });

  watch(
    () => [state.value.currency, state.value.amount],
    ([currency, amount]) => {
      if (currency && amount) loadEstimates();
    },
  );

  watch(
    () => state.value.amount,
    (newValue) => {
      if (newValue < 0) state.value.amount = 0;
    },
  );

  function validateAddress(networkName: string) {
    switch (networkName) {
      case 'BTC':
        return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,39}$/;
      case 'ETH':
      case 'USDTERC20':
      case 'USDTSOL':
      case 'USDTBSC':
      case 'USDC':
        return /^0x[a-fA-F0-9]{40}$/;
      case 'USDTTRC20':
        return /^T[a-zA-Z1-9]{33}$/;
      default:
        return /.*/;
    }
  }

  async function validate(state: IFormData): Promise<FormError[]> {
    const errors: FormError[] = [];

    if (!state.currency) {
      errors.push({ name: 'address', message: 'Please select a currency first' });
    }

    if (!state.address.length) {
      errors.push({ name: 'address', message: 'This field is required' });
    }

    if (state.currency && !validateAddress(state.currency.value)) {
      errors.push({ name: 'address', message: 'Invalid address' });
    }

    if (!state.amount) {
      errors.push({ name: 'amount', message: 'This field is required' });
    }

    if (state.amount && state.amount > maxBalance.value) {
      errors.push({ name: 'amount', message: 'Your vault’s balance is insufficient for this redemption' });
    }

    if (state.amount && state.amount < minWithdrawAmount.value) {
      errors.push({ name: 'amount', message: `Min allowed value is ${minWithdrawAmount.value}` });
    }

    isFormValid.value = !errors.length;

    return errors;
  }

  function setMaxAvailableBalance() {
    state.value.amount = maxBalance.value;
  }

  function resetState() {
    state.value = {
      currency: undefined,
      address: '',
      amount: undefined,
    };
  }

  function resetEstimates() {
    estimatedAmount.value = 0;
    estimatedFeeAmount.value = 0;
  }

  async function getEstimates() {
    if (estimatesLoading.value || !state.value.currency || !state.value.amount) return;

    try {
      estimatesLoading.value = true;

      const data = await cashierStore.getEstimatePayout({
        amount: state.value.amount,
        currency: state.value.currency.value,
      });

      estimatedAmount.value = data?.estimated_amount || 0;
      estimatedFeeAmount.value = data?.redemption_fee || 0;
    } catch (error) {
      console.error(error);
    } finally {
      estimatesLoading.value = false;
    }
  }

  const debouncedGetEstimates = useDebounceFn(getEstimates, 300);

  function loadEstimates() {
    resetEstimates();

    if (!state.value.currency || !isFormValid.value) return;

    debouncedGetEstimates();
  }

  function showRedemptionRequirementsModal() {
    stores.modals.open('redemption-requirements', LazyModalRedemptionRequirements);
  }

  function showRedemptionConfirmationModal() {
    stores.modals.open('redemption-confirmation', LazyModalRedemptionConfirmation);
  }

  async function requestWithdrawal() {
    if (!isWithdrawButtonEnabled.value || !state.value.amount || !state.value.currency?.value) {
      return;
    }

    if (useKYCChecks && !stores.user.isKycApproved) {
      stores.modals.open('kyc', LazyModalKYC);
      return;
    }

    try {
      isWithdrawalRequestPending.value = true;

      const { haserror, status } = (await cashierStore.requestWithdrawal({
        send_address: state.value.address,
        amount: state.value.amount,
        payment_currency: state.value.currency?.value,
      })) as { haserror?: boolean; status: string };

      if (haserror) {
        showErrorToast(status || 'Something went wrong, please try again later');
        return;
      }

      gtag.reportGtagConversion();

      showRedemptionConfirmationModal();
      resetEstimates();
      resetState();
    } catch (error: any) {
      showErrorToast(error.response.data?.status || 'Something went wrong, please try again later');
      console.error(error);
    } finally {
      isWithdrawalRequestPending.value = false;
    }
  }
</script>

<template>
  <div class="w-full flex flex-col gap-4 px-5 mt-6 text-white">
    <CashierCurrencySelector
      v-model:currency="state.currency"
      v-model:open="selectorOpen"
      :list="cashierStore.cryptoCurrencies"
      :class="{ 'mb-[-100%]': selectorOpen }"
    />

    <UForm
      :state="state"
      :validate="validate"
      class="relative w-full flex flex-col -mb-4"
      :class="{
        '-z-1': selectorOpen,
      }"
    >
      <UFormField
        :ui="{
          label: 'text-white font-barlow text-xs font-semibold leading-4 pb-1',
          error: 'mt-0 pt-0.5 font-barlow font-normal text-[11px] leading-[16.5px] break-normal',
        }"
        class="h-[86px]"
        label="Address:"
        name="address"
        eager-validation
      >
        <UInput
          v-model="state.address"
          :ui="{
            base: 'h-[42px] pl-4 font-barlow text-[11px] text-[#101317] rounded-[6px] placeholder:text-[14px] placeholder:text-[#101317]/60 placeholder:font-medium placeholder:leading-4',
          }"
          variant="subtle"
          class="form-input"
          placeholder="Please enter your address"
        />
      </UFormField>
      <UFormField
        :ui="{
          label: 'text-white font-barlow text-xs font-semibold leading-4 pb-1',
          error: 'mt-0 pt-0.5 font-barlow font-normal text-[11px] leading-[16.5px] break-normal',
        }"
        class="h-[86px]"
        name="amount"
        eager-validation
      >
        <template #label>
          <span v-html="amountPlaceholder" />
        </template>
        <UInput
          v-model.number="state.amount"
          :ui="{
            base: 'h-[42px] pl-4 font-barlow text-[14px] text-[#101317] rounded-[6px] placeholder:text-[14px] placeholder:text-[#101317]/60 placeholder:font-medium placeholder:leading-4',
            trailing: 'pe-0 -mr-0.5',
          }"
          type="number"
          variant="subtle"
          class="form-input"
          @keydown="preventNegative"
        >
          <template #trailing>
            <UButton
              :ui="{ base: 'font-barlow text-[14px] font-bold mr-[1px]' }"
              class="w-[66px] h-11"
              @click="setMaxAvailableBalance"
            >
              MAX
            </UButton>
          </template>
        </UInput>
      </UFormField>
    </UForm>

    <!-- Limits -->
    <div
      class="relative font-barlow flex py-2 px-[10px] items-center gap-[10px] self-stretch rounded-6px bg-white/10"
      :class="{
        '-z-1': selectorOpen,
      }"
    >
      <SvgoExclamationCircle class="size-[22px] basis-[22px] shrink-0" filled />
      <div class="flex flex-col items-center gap-[3px] basis-full text-xs leading-4">
        <span class="font-light">Min Redemption</span>
        <span class="font-semibold">${{ minWithdrawAmount }}</span>
      </div>
      <div class="flex flex-col items-center gap-[3px] basis-full text-xs leading-4">
        <span class="font-light">Max Available</span>
        <span class="font-semibold">${{ maxBalance }}</span>
      </div>
    </div>

    <!-- Estimates -->
    <div
      class="relative flex flex-col items-start self-stretch font-barlow text-white -mt-4"
      :class="{
        '-z-1': selectorOpen,
      }"
    >
      <div
        v-if="!useKYCChecks"
        class="flex px-4 py-3 justify-between items-center self-stretch border-b border-white/30 text-xs leading-none"
      >
        <span class="">Estimated Redemption Fees</span>
        <span class="font-semibold">
          {{ estimatedFeeAmount }}
          <template v-if="state.currency">&nbsp;{{ state.currency.text }}</template>
        </span>
      </div>
      <div class="flex px-4 py-3 justify-between items-center self-stretch text-xs leading-none">
        <span>Estimated Redemption</span>
        <div class="flex items-center gap-1">
          <span>
            {{ estimatedAmount }}
            <template v-if="state.currency">{{ state.currency.text }}</template>
          </span>
          <UIcon
            class="size-4 cursor-pointer"
            name="material-symbols:info-outline-rounded"
            @click="showRedemptionRequirementsModal"
          />
        </div>
      </div>
    </div>

    <UButton
      class="relative h-[42px] rounded-6px my-0 disabled:grayscale"
      :class="{
        '-z-1': selectorOpen,
      }"
      :disabled="!isWithdrawButtonEnabled"
      @click="requestWithdrawal"
    >
      <span class="text-white font-barlow text-[15px] font-semibold leading-none">Withdraw</span>
      <UIcon class="size-3" name="fa-solid:chevron-right" />
    </UButton>

    <CashierSupportSection @close="emit('close', {})" />
  </div>
</template>
